import * as THREE from 'three'
import { shaderMaterial } from '~/service/experience/utils/ShaderMaterial'
import { MeshDiscardMaterial } from '~/service/experience/utils/MeshDiscardMaterial'
import Resources from '~/service/experience/utils/Resources'

let plm: ProgressiveLightMap, // class handles the shadow accumulation part
  gLights: THREE.Group, // group containing all the random lights
  gPlane: THREE.Mesh, // shadow catching plane
  shadowMaterial: InstanceType<typeof SoftShadowMaterial>, // instance of SoftShadowMaterial material applied to plane to make only the shadows visible
  resources: Resources

const api = {
  count: 0,
}

function isLight(object: any): object is THREE.Light {
  return object.isLight
}

function isGeometry(object: any): object is THREE.Mesh {
  return !!object.geometry
}

type SoftShadowMaterialProps = {
  map: THREE.Texture | null
  color: THREE.Color
  alphaTest: number
  opacity: number
  blend: number
}

const SoftShadowMaterial = shaderMaterial<SoftShadowMaterialProps>(
  {
    color: new THREE.Color(0x000000),
    blend: 1.0,
    alphaTest: 0.75,
    opacity: 0.2,
    map: null,
  },
  `varying vec2 vUv;
   void main() {
     gl_Position = projectionMatrix * viewMatrix * modelMatrix * vec4(position, 1.);
     vUv = uv;
   }`,
  `varying vec2 vUv;
   uniform sampler2D map;
   uniform vec3 color;
   uniform float opacity;
   uniform float alphaTest;
   uniform float blend;
   void main() {
     vec4 sampledDiffuseColor = texture2D(map, vUv);
     gl_FragColor = vec4(color * sampledDiffuseColor.r * blend, max(0.0, (1.0 - (sampledDiffuseColor.r + sampledDiffuseColor.g + sampledDiffuseColor.b) / alphaTest)) * opacity);
     #include <tonemapping_fragment>
     #include <${
       parseInt(THREE.REVISION.replace(/\D+/g, '')) >= 154
         ? 'colorspace_fragment'
         : 'encodings_fragment'
     }>
   }`
)

class ProgressiveLightMap {
  renderer: THREE.WebGLRenderer
  res: number
  scene: THREE.Scene
  object: THREE.Mesh | null
  buffer1Active: boolean
  progressiveLightMap1: THREE.WebGLRenderTarget
  progressiveLightMap2: THREE.WebGLRenderTarget
  discardMat: THREE.ShaderMaterial
  targetMat: THREE.MeshLambertMaterial
  previousShadowMap: { value: THREE.Texture }
  averagingWindow: { value: number }
  clearColor: THREE.Color
  clearAlpha: number
  lights: { object: THREE.Light; intensity: number }[]
  meshes: { object: THREE.Mesh; material: THREE.Material | THREE.Material[] }[]

  constructor(
    renderer: THREE.WebGLRenderer,
    scene: THREE.Scene,
    res: number = 1024
  ) {
    this.renderer = renderer
    this.res = res
    this.scene = scene
    this.buffer1Active = false
    this.lights = []
    this.meshes = []
    this.object = null
    this.clearColor = new THREE.Color()
    this.clearAlpha = 0

    // Create the Progressive LightMap Texture
    const format = /(Android|iPad|iPhone|iPod)/g.test(navigator.userAgent)
      ? THREE.HalfFloatType
      : THREE.FloatType
    this.progressiveLightMap1 = new THREE.WebGLRenderTarget(
      this.res,
      this.res,
      {
        type: format,
      }
    )
    this.progressiveLightMap2 = new THREE.WebGLRenderTarget(
      this.res,
      this.res,
      {
        type: format,
      }
    )

    // Inject some spicy new logic into a standard phong material
    this.discardMat = new MeshDiscardMaterial()
    this.targetMat = new THREE.MeshLambertMaterial({ fog: false })
    this.previousShadowMap = { value: this.progressiveLightMap1.texture }
    this.averagingWindow = { value: 100 }
    this.targetMat.onBeforeCompile = (shader) => {
      // Vertex Shader: Set Vertex Positions to the Unwrapped UV Positions
      shader.vertexShader =
        'varying vec2 vUv;\n' +
        shader.vertexShader.slice(0, -1) +
        'vUv = uv; gl_Position = vec4((uv - 0.5) * 2.0, 1.0, 1.0); }'

      // Fragment Shader: Set Pixels to average in the Previous frame's Shadows
      const bodyStart = shader.fragmentShader.indexOf('void main() {')
      shader.fragmentShader =
        'varying vec2 vUv;\n' +
        shader.fragmentShader.slice(0, bodyStart) +
        'uniform sampler2D previousShadowMap;\n	uniform float averagingWindow;\n' +
        shader.fragmentShader.slice(bodyStart - 1, -1) +
        `\nvec3 texelOld = texture2D(previousShadowMap, vUv).rgb;
        gl_FragColor.rgb = mix(texelOld, gl_FragColor.rgb, 1.0/ averagingWindow);
      }`

      // Set the Previous Frame's Texture Buffer and Averaging Window
      shader.uniforms.previousShadowMap = this.previousShadowMap
      shader.uniforms.averagingWindow = this.averagingWindow
    }
  }

  clear() {
    this.renderer.getClearColor(this.clearColor)
    this.clearAlpha = this.renderer.getClearAlpha()
    this.renderer.setClearColor('black', 1)
    this.renderer.setRenderTarget(this.progressiveLightMap1)
    this.renderer.clear()
    this.renderer.setRenderTarget(this.progressiveLightMap2)
    this.renderer.clear()
    this.renderer.setRenderTarget(null)
    this.renderer.setClearColor(this.clearColor, this.clearAlpha)

    this.lights = []
    this.meshes = []

    this.scene.traverse((object) => {
      if (isGeometry(object)) {
        this.meshes.push({ object, material: object.material })
      } else if (isLight(object)) {
        this.lights.push({ object, intensity: object.intensity })
      }
    })
  }

  prepare() {
    this.lights.forEach((light) => (light.object.intensity = 0))
    this.meshes.forEach((mesh) => (mesh.object.material = this.discardMat))
  }

  finish() {
    this.lights.forEach((light) => (light.object.intensity = light.intensity))
    this.meshes.forEach((m) => {
      if (
        m.material.name &&
        m.material.name.length &&
        !Number.isNaN(+m.material.name)
      ) {
        m.object.material = m.material
        resources.getTexture(+m.material.name, (texture: THREE.Texture) => {
          m.object.material = new THREE.MeshStandardMaterial({
            map: texture.clone(),
          })

          return this
        })
      } else {
        m.object.material = m.material
      }
    })
  }

  configure(object: THREE.Mesh) {
    this.object = object
  }

  update(camera: THREE.Camera, blendWindow = 100) {
    if (!this.object) return
    // Set each object's material to the UV Unwrapped Surface Mapping Version
    this.averagingWindow.value = blendWindow
    this.object.material = this.targetMat
    // Ping-pong two surface buffers for reading/writing
    const activeMap = this.buffer1Active
      ? this.progressiveLightMap1
      : this.progressiveLightMap2
    const inactiveMap = this.buffer1Active
      ? this.progressiveLightMap2
      : this.progressiveLightMap1
    // Render the object's surface maps
    this.renderer.setRenderTarget(activeMap)
    this.previousShadowMap.value = inactiveMap.texture
    this.buffer1Active = !this.buffer1Active
    this.renderer.render(this.scene, camera)
    this.renderer.setRenderTarget(null)

    this.object.material = shadowMaterial
  }
}

const shadowParams = {
  /** Temporal accumulates shadows over time which is more performant but has a visual regression over instant results, false  */
  temporal: true,

  /** How many frames it can render, more yields cleaner results but takes more time, 40 */
  frames: 40,

  /** Can limit the amount of frames rendered if frames === Infinity, usually to get some performance back once a movable scene has settled, Infinity */
  limit: Infinity,

  /** If frames === Infinity blend controls the refresh ratio, 100 */
  blend: 40,

  /** Scale of the plane,  */
  scale: 10,

  /** Opacity of the plane, 1 */
  opacity: 0.2,

  /** Discards alpha pixels, 0.65 */
  alphaTest: 0.98,

  /** ColorBlend, how much colors turn to black, 0 is black, 2 */
  colorBlend: 2,
}

/**
 * Shadow properties & common dir light properties
 */
const lightParams = {
  /** Light position */
  position: new THREE.Vector3(0, 9, 0),

  /** Radius of the jiggle, higher values make softer light */
  radius: 7,

  /** Amount of lights*/
  amount: 8,

  /** Light intensity */
  intensity: parseInt(THREE.REVISION.replace(/\D+/g, '')) >= 155 ? Math.PI : 1,

  /** Ambient occlusion, lower values mean less AO, hight more, you can mix AO and directional light */
  ambient: 0.5,

  bias: 0.001, //shadow bias
  mapSize: 512, // shadow map res
  size: 20, // shadow camera top,bottom,left,right
  near: 0.1, // shadow camera near
  far: 200, // shadow camera far
}

let renderer: THREE.WebGLRenderer, scene: THREE.Scene, camera: THREE.Camera

export function useAccumulativeShadows() {
  const setupAccumulativeShadows = (
    _renderer: THREE.WebGLRenderer,
    _scene: THREE.Scene,
    _camera: THREE.Camera,
    _resources: Resources
  ) => {
    renderer = _renderer
    scene = _scene
    camera = _camera
    resources = _resources

    plm = new ProgressiveLightMap(renderer, scene, 2048)

    // Material applied to the shadow-catching plane
    shadowMaterial = new SoftShadowMaterial({
      map: plm.progressiveLightMap2.texture, // Ignore TypeScript error, this texture contains the rendered shadow image
      transparent: true,
      depthWrite: false,
      toneMapped: true,
      blend: shadowParams.colorBlend, // Color blend
      alphaTest: 0, // Set to 0 so that the first frame, where nothing is computed, does not show a black plane
    })

    // shadow catching plane
    gPlane = new THREE.Mesh(
      new THREE.PlaneGeometry(20, 20).rotateX(-Math.PI / 2),
      shadowMaterial
    )
    gPlane.position.y = 0.005
    gPlane.receiveShadow = true
    scene.add(gPlane)

    // connect plane to ProgressiveLightMap class
    plm.configure(gPlane)

    // create group to hold the lights
    gLights = new THREE.Group()

    // create directional lights to speed up the convergence
    for (let l = 0; l < lightParams.amount; l++) {
      const dirLight = new THREE.DirectionalLight(
        0xffffff,
        lightParams.intensity / lightParams.amount
      )
      dirLight.name = 'dir_light_' + l
      dirLight.castShadow = true
      dirLight.shadow.bias = lightParams.bias
      dirLight.shadow.camera.near = lightParams.near
      dirLight.shadow.camera.far = lightParams.far
      dirLight.shadow.camera.right = lightParams.size / 2
      dirLight.shadow.camera.left = -lightParams.size / 2
      dirLight.shadow.camera.top = lightParams.size / 2
      dirLight.shadow.camera.bottom = -lightParams.size / 2
      dirLight.shadow.mapSize.width = lightParams.mapSize
      dirLight.shadow.mapSize.height = lightParams.mapSize
      gLights.add(dirLight)
    }
  }

  function randomiseLightPositions() {
    const vLength = lightParams.position.length()

    for (let i = 0; i < gLights.children.length; i++) {
      const light = gLights.children[i]
      if (Math.random() > lightParams.ambient) {
        light.position.set(
          lightParams.position.x +
            THREE.MathUtils.randFloatSpread(lightParams.radius),
          lightParams.position.y +
            THREE.MathUtils.randFloatSpread(lightParams.radius),
          lightParams.position.z +
            THREE.MathUtils.randFloatSpread(lightParams.radius)
        )
      } else {
        let lambda = Math.acos(2 * Math.random() - 1) - Math.PI / 2.0
        let phi = 2 * Math.PI * Math.random()
        light.position.set(
          Math.cos(lambda) * Math.cos(phi) * vLength,
          Math.abs(Math.cos(lambda) * Math.sin(phi) * vLength),
          Math.sin(lambda) * vLength
        )
      }
    }
  }

  function reset() {
    plm.clear()
    shadowMaterial.opacity = 0
    shadowMaterial.alphaTest = 0
    api.count = 0

    // If temporal is disabled and a finite number of frames is specified,
    // accumulate all frames in one shot. Expect the renderer to freeze while it's computing.
    if (!shadowParams.temporal && shadowParams.frames !== Infinity) {
      renderShadows(shadowParams.frames)
    }
  }

  function temporalUpdate() {
    // If temporal is true, accumulate one frame at a time, as long as the count is within the specified limit
    if (
      (shadowParams.temporal || shadowParams.frames === Infinity) &&
      api.count < shadowParams.frames &&
      api.count < shadowParams.limit
    ) {
      renderShadows()
      api.count++
    }
  }

  function renderShadows(frames = 1) {
    shadowParams.blend = Math.max(
      2,
      shadowParams.frames === Infinity
        ? shadowParams.blend
        : shadowParams.frames
    )

    // Adapt the opacity-blend ratio to the number of frames
    if (!shadowParams.temporal) {
      shadowMaterial.opacity = shadowParams.opacity
      shadowMaterial.alphaTest = shadowParams.alphaTest
    } else {
      shadowMaterial.opacity = Math.min(
        shadowParams.opacity,
        shadowMaterial.opacity + shadowParams.opacity / shadowParams.blend
      )
      shadowMaterial.alphaTest = Math.min(
        shadowParams.alphaTest,
        shadowMaterial.alphaTest + shadowParams.alphaTest / shadowParams.blend
      )
    }

    // Switch accumulative lights on
    scene.add(gLights)
    // Collect scene lights and meshes
    plm.prepare()
    // Update the lightmap and the accumulative lights
    for (let i = 0; i < frames; i++) {
      randomiseLightPositions()
      plm.update(camera, shadowParams.blend)
    }
    // Switch lights off
    scene.remove(gLights)
    // Restore lights and meshes
    plm.finish()
  }

  return {
    setupAccumulativeShadows,
    reset,
    temporalUpdate,
  }
}

export { SoftShadowMaterial, ProgressiveLightMap }
